import { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';

import { formatMonthNum } from '../../../../utils/timeUtils';
import { sentenceCase } from '../../../../utils/utils';
import ActionButton from '../../../elements/action-button/ActionButton';
import Modal from '../../../sections/modal/Modal';

const UpdateCardModal = ({ card, visible, onClose, onConfirm }) => {
  const { t } = useTranslation();

  const schema = object({
    name: string()
      .required(
        sentenceCase(
          t('inputRequired', {
            ns: 'validations',
            field: t('creditCardHolder', { ns: 'fields' }),
          })
        )
      )
      .max(50, t('maxLength', { ns: 'validations', max: '50' })),
    expDate: string()
      .required(
        sentenceCase(
          t('inputRequired', {
            ns: 'validations',
            field: t('creditCardExpiration', { ns: 'fields' }),
          })
        )
      )
      .matches(
        /^(0[1-9]|1[0-2])\/([2-9][0-9])$/,
        sentenceCase(
          `${t('invalidFormat', {
            ns: 'validations',
            field: t('creditCardExpiration', { ns: 'fields' }),
          })} ${t('example', { ns: 'validations', example: '09/23' })}`
        )
      ),
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    shouldUnregister: true,
  });

  const confirmUpdate = async (data) => {
    const payload = {
      name: data.name,
      expireMonth: data.expDate.slice(0, 2),
      expireYear: `20${data.expDate.slice(3)}`,
    };

    await onConfirm(payload);
  };

  useEffect(() => {
    if (visible) reset();
  }, [visible]);

  return (
    <Modal
      openModal={visible}
      title={t('update', {
        ns: 'project',
        text: t('billing.creditCardInfo', { ns: 'project' }),
      })}
      handleClose={onClose}
    >
      <form autoComplete="off">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Controller
            name="name"
            control={control}
            defaultValue={card?.name || ''}
            render={({ field }) => (
              <TextField
                {...field}
                id="name"
                label={t('creditCardHolder', { ns: 'fields' })}
                error={!!errors?.name}
                helperText={errors?.name?.message}
                fullWidth
                size="small"
                inputProps={{ maxLength: 50 }}
              />
            )}
          />

          <Controller
            name="expDate"
            control={control}
            defaultValue={`${formatMonthNum(card?.expireMonth)}/${card?.expireYear
              .toString()
              .slice(2)}`}
            render={({ field }) => (
              <TextField
                {...field}
                id="exp-date"
                label={`${t('creditCardExpiration', { ns: 'fields' })}${t(
                  'billing.expExample',
                  { ns: 'project' }
                )}`}
                error={!!errors?.expDate}
                helperText={errors?.expDate?.message}
                fullWidth
                size="small"
                inputProps={{ maxLength: 5 }}
              />
            )}
          />
        </Box>

        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 2 }}
        >
          <Button variant="text" onClick={onClose}>
            {t('cancel', { ns: 'buttons' })}
          </Button>
          <ActionButton
            clickAction={handleSubmit(confirmUpdate)}
            disabled={!isDirty || !isValid}
          >
            {t('confirm', { ns: 'buttons' })}
          </ActionButton>
        </Box>
      </form>
    </Modal>
  );
};

UpdateCardModal.propTypes = {
  card: PropTypes.object,
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default UpdateCardModal;
